import { Node as TiptapNode, mergeAttributes, Editor } from '@tiptap/core';
import { GapCursor } from '@tiptap/pm/gapcursor';
import { Node } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react';
import {
  CustomSectionState,
  CustomSectionContentState,
} from 'api/custom-section/types';
import { ComponentType } from 'react';
import { v4 } from 'uuid';
import {
  RlCustomSectionName,
  RlGroupSectionName,
} from '../custom-block-node-names';
import { CustomGroupSectionNodeView } from './custom-node-view';
import {
  AddCustomGroupSectionPayload,
  CustomGroupSectionNodeOptions,
  RemoveCustomGroupSectionPayload,
} from './types';
import { createCustomSectionTransactionFilter } from '../custom-section/utils/transaction-filter';

export { RlGroupSectionName } from '../custom-block-node-names';
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [RlGroupSectionName]: {
      addCustomGroupSection: (
        payload: AddCustomGroupSectionPayload,
      ) => ReturnType;
      removeCustomGroupSection: (
        payload: RemoveCustomGroupSectionPayload,
      ) => ReturnType;
    };
  }
}
const isNodeEmpty = (node: Node) => {
  if (
    node.type.name === RlGroupSectionName &&
    node.attrs.contentState === CustomSectionContentState.Prompt
  ) {
    return false;
  }

  return node.content.size === 0;
};
const scrollInsertedNodeIntoView = (editor: Editor, insertPosition: number) => {
  setTimeout(() => {
    const node = editor.view.nodeDOM(insertPosition) as HTMLElement;

    if (node) {
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, 0);
};
// TOOD: split this file content into multiple files

export const RlGroupSection = TiptapNode.create<CustomGroupSectionNodeOptions>({
  name: RlGroupSectionName,
  group: 'block',
  content: 'block*',

  // Keep only one instance of each property
  isolating: true,
  defining: true,
  draggable: true,
  selectable: false,
  allowGapCursor: true,

  marks: '',
  atom: false,

  addKeyboardShortcuts() {
    const deleteNodesInSelection = (editor: Editor): boolean => {
      const { state, view } = editor;
      const { selection } = state;

      if (selection.empty) {
        return false;
      }

      const { from, to } = selection;
      let deleteFrom = from;
      let deleteTo = to;
      let found = false;

      state.doc.nodesBetween(from, to, (node, pos) => {
        if (
          node.type.name === RlGroupSectionName ||
          node.type.name === RlCustomSectionName
        ) {
          // Skip if the selection is entirely inside this node (it's a parent, not a sibling).
          // nodesBetween visits ancestor nodes too, so without this guard a text-only
          // selection within a group or custom section would expand the delete range to the whole node.
          if (pos < from && pos + node.nodeSize > to) {
            return false;
          }

          deleteFrom = Math.min(deleteFrom, pos);
          deleteTo = Math.max(deleteTo, pos + node.nodeSize);
          found = true;

          return false;
        }

        return true;
      });

      if (!found) {
        return false;
      }

      view.dispatch(state.tr.delete(deleteFrom, deleteTo));

      return true;
    };

    return {
      Backspace: ({ editor }) => {
        const { state, view } = editor;
        const { selection, doc } = state;
        const { empty, from } = selection;

        if (!empty) {
          return deleteNodesInSelection(editor);
        }

        const exactSelectionPosition = doc.resolve(from - 1);
        const node = exactSelectionPosition.node();

        if (node == null || node.type.name !== this.name) {
          return false;
        }

        const atStartOfNode = exactSelectionPosition.parentOffset === 0;
        const atEndOfNode =
          exactSelectionPosition.parentOffset === node.content.size;

        if (atStartOfNode) {
          const beforeNodePos = exactSelectionPosition.before();
          const $beforeNode = doc.resolve(beforeNodePos);
          const nodeBefore = $beforeNode.nodeBefore;

          if (nodeBefore && nodeBefore.type.name === this.name) {
            view.dispatch(
              state.tr.setSelection(new GapCursor(doc.resolve(beforeNodePos))),
            );

            return true;
          }

          if (nodeBefore) {
            const endOfNodeBeforePos = beforeNodePos - 1;

            if (endOfNodeBeforePos >= 0) {
              view.dispatch(
                state.tr.setSelection(
                  TextSelection.near(doc.resolve(endOfNodeBeforePos)),
                ),
              );

              return true;
            }
          }

          return false;
        }

        if (atEndOfNode) {
          return editor.commands.removeCustomSection({
            nodeAt: exactSelectionPosition.before(),
          });
        }

        return false;
      },
      Delete: ({ editor }) => deleteNodesInSelection(editor),
    };
  },
  addCommands() {
    return {
      addCustomGroupSection:
        (payload: AddCustomGroupSectionPayload) =>
        ({ editor, tr }) => {
          const customSectionNode = editor.schema.nodeFromJSON({
            type: this.name,
            attrs: {
              ...payload.attributes,
              templateSectionId:
                payload.attributes.templateSectionId ??
                (this.options.generateTemplateSectionId ? v4() : null),
            },
            content: [
              {
                type: 'paragraph',
              },
            ],
          });
          const $from = tr.doc.resolve(payload.insertAt ?? tr.selection.from);

          if ($from.depth === 0) {
            const docNode = $from.node();

            if (
              docNode.childCount === 1 &&
              docNode.child(0).type.name === 'paragraph' &&
              docNode.child(0).content.size === 0
            ) {
              const emptyParagraphPos = $from.start(0);

              tr.replaceRangeWith(
                emptyParagraphPos,
                emptyParagraphPos + docNode.child(0).nodeSize,
                customSectionNode,
              );
              scrollInsertedNodeIntoView(editor, emptyParagraphPos);

              return true;
            }

            const prevNode = $from.nodeBefore;

            if (
              prevNode &&
              prevNode.type.name === 'paragraph' &&
              prevNode.content.size === 0
            ) {
              const prevPos = $from.pos - prevNode.nodeSize;

              tr.deleteRange(prevPos, $from.pos);
              tr.insert(prevPos, customSectionNode);
              scrollInsertedNodeIntoView(editor, prevPos);

              return true;
            }

            tr.insert($from.pos, customSectionNode);
            scrollInsertedNodeIntoView(editor, $from.pos);

            return true;
          }

          const parentNode = $from.node(1);

          if (isNodeEmpty(parentNode)) {
            const fromPos = $from.start(1);
            const toPos = $from.end(1);

            tr.replaceRangeWith(
              fromPos,
              Math.min(toPos, editor.state.doc.content.size),
              customSectionNode,
            );
            scrollInsertedNodeIntoView(editor, fromPos);

            return true;
          }

          tr.insert(
            Math.min($from.end(1) + 1, editor.state.doc.content.size),
            customSectionNode,
          );
          scrollInsertedNodeIntoView(editor, $from.end(1) + 1);

          return true;
        },
      removeCustomGroupSection:
        (payload: RemoveCustomGroupSectionPayload) =>
        ({ tr }) => {
          const node = tr.doc.nodeAt(payload.nodeAt);

          if (node) {
            tr.deleteRange(payload.nodeAt, payload.nodeAt + node.nodeSize);
          }

          return true;
        },
    };
  },
  addOptions() {
    return {
      allowResolve: false,
      generateTemplateSectionId: false,
      companyPageDerivedSectionSourcesUpdater: undefined,
      validPositions: {
        beforeMatch: ['paragraph'],
        afterMatch: ['paragraph'],
      },
      allowsNesting: false,
      pageType: undefined,
    };
  },
  addAttributes() {
    return {
      templateSectionId: {
        default: null,
        parseHTML: (element) => {
          const value = element.getAttribute('data-template-section-id');

          return value ? Number(value) : null;
        },
        renderHTML: (attributes) => {
          return {
            ['data-template-section-id']: attributes.templateSectionId,
          };
        },
      },
      state: {
        default: CustomSectionState.Closed,
      },
      contentState: {
        default: CustomSectionContentState.Generated,
        parseHTML: (element) => {
          return element.getAttribute('data-content-state');
        },
        renderHTML: (attributes) => {
          return {
            ['data-content-state']: attributes.contentState,
          };
        },
      },
      sectionType: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute('data-section-type');
        },
        renderHTML: (attributes) => {
          return {
            ['data-section-type']: attributes.sectionType,
          };
        },
      },
      title: {
        default: '',
      },
      config: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('data-config');

          if (!raw) {
            return null;
          }
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        },
        renderHTML: (attrs) =>
          attrs.config ? { 'data-config': JSON.stringify(attrs.config) } : {},
      },
      filters: {
        default: null,
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: `div[data-type="${this.name}"]`,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        ['data-type']: this.name,
      }),
      0,
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(
      CustomGroupSectionNodeView as unknown as ComponentType<
        ReactNodeViewProps<HTMLElement>
      >,
      {
        as: 'section',
        className: 'custom-section',
        attrs: ({ node }) => {
          return {
            ['data-content-state']: node.attrs.contentState.toLowerCase(),
            draggable: 'true',
            style: 'cursor: grab',
          };
        },
      },
    );
  },
  topNode: true,

  addProseMirrorPlugins() {
    return [
      createCustomSectionTransactionFilter({
        nodeName: this.name,
        validPositions: this.options.validPositions,
        allowedNeighborNodes: [RlCustomSectionName],
      }),
    ];
  },
});
